import React from "react";
import Modal from "../../../components/common/Modal";
import ContentToggle from "../../../components/common/ContentToggle";

interface SelectUBTHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * SelectUBTHelpModal - Modal help dialog for the "Find and Select UBT" feature.
 *
 * Displays frequently asked questions about the Select UBT page using
 * expandable ContentToggle sections. Opened from the Find and Select UBT page
 * help popup when the user clicks "additional page help".
 */
const SelectUBTHelpModal: React.FC<SelectUBTHelpModalProps> = ({
  isOpen,
  onClose,
}) => {
  const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 100% !important;
    width: 100% !important;
    top: 10% !important;
  }
`;
  return (
    <Modal
      isOpen={isOpen}
      title="Select UBT Questions & Answers"
      onClose={onClose}
      onSecondary={onClose}
      clearBtn="Close"
      contentClassName="mb-4!"
    >
      <div className="py-2">
        <ContentToggle
          titleText="What's new with select UBT page?"
          isFillOption
        >
          <p className="text-[14px]!">
            UBT Tracker now gives you access to project information from all
            regions and locations. This is a powerful feature that you can use
            to learn best practices and share content knowledge outside your
            region.
          </p>
          <p className="mt-3! text-[14px]!">
            Although you have cross-regional view rights, you need to search for
            teams in each region separately. There is no &ldquo;all
            regions&rdquo; value in any location field. You may not edit
            teams&rsquo; information from other regions.
          </p>
        </ContentToggle>

        <ContentToggle titleText="How do I search for teams?" isFillOption>
          <p className="text-[14px]!">
            The Select UBT Page offers different ways to search for teams based
            on location, multiple cost centers, or UBT name. For example, a
            facility administrator could search all teams by location, while a
            pharmacist can search for other pharmacy departments&rsquo; best
            practices.
          </p>
        </ContentToggle>

        <ContentToggle titleText="How do I sort my results?" isFillOption>
          <p className="text-[14px]!">
            Click on the column header to sort the data from your search result.
            For example, click on the &ldquo;Cost Center&rdquo; column header to
            sort teams in ascending numerical cost center order. Click the
            column a second time to reverse the order of your selected sort. An
            arrow is displayed in the column that has been used to sort the
            displayed data. An up arrow means sort values
            &ldquo;increase&rdquo;.
          </p>
          <p className="mt-3! text-[14px]! font-semibold! underline!">
            All filters applied to your search on this page will be kept when
            you move to other pages, such as Search Project.
          </p>
          <p className="mt-2! text-[14px]!">
            Example: If you only have selected UBT ID #1 through #5, all
            projects listed in the Search Project Page will be associated with
            either one of these 5 teams. You can change the filter options at any time and click the Search button to update your results.
          </p>
          <p className="mt-3! text-[14px]!">
            See Also:{" "}
            <a
              href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              https://www.lmpartnership.org/tools/ubt-tracker-user-guide
            </a>
          </p>
        </ContentToggle>
      </div>
      <style>{MODAL_STYLES}</style>
    </Modal>
  );
};

export default SelectUBTHelpModal;
